import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDeleteFolderDocument } from 'modules/documents/hooks/useDestroyFolder'
import { Folder, useGetFolders } from 'modules/documents/hooks/useGetFolders'
import Loading from 'modules/layout/components/Page/Loading'

interface Props {
  folder: Folder
  onClose: () => void
  companyId: string
  navigateTo?: string
}

const RemoveFolderSubfolderModal = ({ folder, onClose, companyId, navigateTo }: Props) => {
  const navigate = useNavigate()
  const [open, toggle] = useToggle(false)

  const { folders: descendantFolders, loading: loadingDescendantFolders } = useGetFolders(
    {
      ascendantFolderId: folder.id,
      withUnfilteredCounters: true,
    },
    'no-cache'
  )
  const { folders: currentFolders, loading: loadingCurrentFolders } = useGetFolders(
    {
      ids: [folder.id],
      withUnfilteredCounters: true,
    },
    'no-cache'
  )

  const { loading: deleting, deleteFolder } = useDeleteFolderDocument(folder, companyId)

  const onDelete = async () => {
    const result = await deleteFolder()

    const resultHandled = handleResult(result, () => undefined)

    if (resultHandled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully') })
      onClose()

      if (navigateTo) {
        navigate(navigateTo)
      }
    }
  }

  const foldersWithDocuments = useUnsafeMemo(() => {
    const folders = [...currentFolders, ...descendantFolders]

    return folders.filter((folder_elem) => folder_elem.counterDocuments > 0)
  }, [currentFolders, descendantFolders])

  const disabled = deleting || !hasFeature('custom_folders') || foldersWithDocuments.length > 0

  const loading = loadingCurrentFolders || loadingDescendantFolders

  const folderDeleteWarning = () => {
    return (
      <Tooltip.Alt
        attachment="bottom center"
        targetAttachment="top center"
        toggle={toggle}
        open={open}
        size="auto"
        label={
          <Box paddingY="s12" paddingX="s16" gap="s16" width="full">
            <Text.Normal size="100">
              {i18n.t('settings.folders.form.delete_with_subfolders.tooltip.title')}
            </Text.Normal>
            <Box textAlign="left">
              <Text.Normal size="100">
                {foldersWithDocuments.map((f) => f.name).join(', ')}
              </Text.Normal>
            </Box>
          </Box>
        }
      >
        <Box flexDirection="row" alignItems="center" gap="s4">
          <Alert color={'error'}>
            <Box>
              <Text.Normal>
                {i18n.t('settings.folders.form.delete_with_subfolders.warning')}
              </Text.Normal>
            </Box>
          </Alert>
        </Box>
      </Tooltip.Alt>
    )
  }

  const description = () => {
    return (
      <Box gap="s24" flexDirection="column">
        {i18n.t('settings.folders.form.delete_with_subfolders.description', {
          name: folder.name,
        })}
        {!loading && foldersWithDocuments.length > 0 && folderDeleteWarning()}
        {loading && <Loading />}
      </Box>
    )
  }

  return (
    <Modal
      illustration="delete"
      title={i18n.t('settings.folders.form.delete_with_subfolders.title')}
      onClose={onClose}
      description={description()}
      onSubmit={onDelete}
      footer={
        <Box gap="s4" flexDirection="row">
          <Button onClick={onClose} type={'secondary'}>
            {i18n.t('settings.folders.form.delete_with_subfolders.cancel')}
          </Button>

          <Button disabled={disabled || loading} type="negative" submit>
            {i18n.t('settings.folders.form.delete_with_subfolders.button')}
          </Button>
        </Box>
      }
    ></Modal>
  )
}

export default observer(RemoveFolderSubfolderModal)
